import setupCreditsPlansTooltips from "app/components/credits_plans_tooltips"
import {
  trackCustomPlanRequestClicked,
  trackCustomPlanRequestShown,
  trackSubscriptionPaywallShown,
} from "app/utils/mixpanel_tracking"
import onFirstView from "app/utils/on_first_view"
import openSupportConversation from "app/utils/support_conversation"
import { onBFCacheLoad } from "shared/scripts/back_forward_cache_utils"

export const CUSTOM_PLAN_REQUEST_TAG = "custom_plan_request"
export const CUSTOM_PLAN_REQUEST_MESSAGE = "Hi! I'm interested in a plan tailored to my needs."

type Interval = "monthly" | "yearly"
type PerInterval = Record<Interval, string>

interface Payload {
  prices: Record<string, PerInterval>
  urls: Record<string, PerInterval>
}

// "Contact us" below the cards. Signed out it is a link to the login, which returns to /plans.
const setupCustomPlanRequest = () => {
  const cta = document.querySelector<HTMLElement>("[data-custom-plan-request]")
  if (!cta) return

  const payload = { location: "Plans", tier: window.App.user?.tier ?? "" } as const

  onFirstView(cta.closest(".credits-plans-custom") ?? cta, () => trackCustomPlanRequestShown(payload))
  cta.addEventListener("click", () => {
    trackCustomPlanRequestClicked(payload)
    if (cta instanceof HTMLButtonElement) {
      openSupportConversation({ tag: CUSTOM_PLAN_REQUEST_TAG, message: CUSTOM_PLAN_REQUEST_MESSAGE })
    }
  })
}

// Upgrade links to /plans carry ?entrypoint=; the visit event keeps it, so a click on a paused chip
// or notice stays attributed after the page changes. Signed-out visits are not a paywall. Back from
// checkout restores the page from the back/forward cache without running scripts, so that counts too.
const trackPlansVisit = () => {
  if (!window.App?.user) return

  const entrypoint = new URLSearchParams(window.location.search).get("entrypoint") ?? undefined
  trackSubscriptionPaywallShown({ location: "Plans", reason: "Visit", entrypoint })
}

// Monthly/yearly switch of the credits /plans page. `prices` and `urls` are keyed by checkout
// key (starter, pro, pro_plus). Checkout links, the card prices and the "billed annually" lines
// follow the switch.
export default function CreditsPlans({ prices, urls }: Payload) {
  setupCreditsPlansTooltips()
  setupCustomPlanRequest()
  trackPlansVisit()
  onBFCacheLoad("credits-plans-visit", trackPlansVisit)

  const toggle = document.querySelector<HTMLButtonElement>(".credits-plans-toggle .switch-field")
  if (!toggle) return

  const labels = document.querySelectorAll<HTMLElement>(".credits-plans-toggle-label")

  toggle.addEventListener("click", () => {
    const yearly = toggle.getAttribute("aria-checked") !== "true"
    const interval: Interval = yearly ? "yearly" : "monthly"

    toggle.setAttribute("aria-checked", String(yearly))
    toggle.classList.toggle("switch-field-on", yearly)
    labels.forEach(label => label.classList.toggle("is-active", label.dataset.interval === interval))
    document.querySelectorAll<HTMLElement>("[data-plan-billing]").forEach(line => {
      line.hidden = !yearly
    })

    Object.keys(prices).forEach(plan => {
      document.querySelectorAll<HTMLAnchorElement>(`a[data-plan-tier='${plan}']`).forEach(link => {
        link.href = urls[plan][interval]
      })
      document.querySelectorAll<HTMLElement>(`[data-plan-price='${plan}']`).forEach(price => {
        price.textContent = prices[plan][interval]
      })
    })
  })
}
